import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, KeyRound, UserCog, Lock, Unlock, Download, FileText, Users2,
  Loader2, RefreshCw, Fingerprint, PowerOff, Power, Search, Activity,
  Server, Database, Cpu, HardDrive, Radio, CheckCircle2, XCircle, PlayCircle,
  ToggleLeft, ToggleRight, BadgeCheck, Clock, ExternalLink, AlertTriangle,
} from "lucide-react";

const TABS = [
  { key: "users", label: "Identity Administration", icon: Users2 },
  { key: "sessions", label: "Session Management", icon: Lock },
  { key: "audit", label: "Audit & Compliance", icon: FileText },
  { key: "evidence", label: "Evidence Packs", icon: ShieldCheck },
  { key: "federation", label: "Identity Federation", icon: Fingerprint },
  { key: "health", label: "Deployment Health", icon: Activity },
  { key: "infra", label: "Infrastructure Health", icon: Server },
  { key: "monitoring", label: "Production Monitoring", icon: Radio },
  { key: "validation", label: "Security Validation", icon: BadgeCheck },
  { key: "flags", label: "Rollback & Flags", icon: ToggleRight },
  { key: "enterprise-trust", label: "Enterprise Trust", icon: ShieldCheck },
  { key: "tsa", label: "Timestamp Authority", icon: Clock },
  { key: "security-monitoring", label: "Security Monitoring", icon: Radio },
  { key: "cert-lifecycle", label: "Certificate Lifecycle", icon: KeyRound },
  { key: "disaster-recovery", label: "Disaster Recovery", icon: HardDrive },
  { key: "production-audit", label: "Production Audit", icon: BadgeCheck },
];

const KPI = ({ label, value, hint, testid }) => (
  <div className="cad-panel p-4" data-testid={testid}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{value}</div>
    {hint && <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">{hint}</div>}
  </div>
);

export const AdminConsole = () => {
  const qc = useQueryClient();
  const [tab, setTab] = useState("users");
  const [q, setQ] = useState("");
  const [lastReset, setLastReset] = useState(null);

  const users = useQuery({
    queryKey: ["admin-users", q],
    queryFn: async () => (await api.get("/cadastre360/admin/users", { params: { q: q || undefined, limit: 200 } })).data,
  });
  const audit = useQuery({
    queryKey: ["admin-audit"],
    queryFn: async () => (await api.get("/cadastre360/admin/audit")).data,
    enabled: tab === "audit",
  });
  const providers = useQuery({
    queryKey: ["admin-providers"],
    queryFn: async () => (await api.get("/cadastre360/admin/federation/providers")).data,
    enabled: tab === "federation",
  });
  const health = useQuery({
    queryKey: ["admin-health"],
    queryFn: async () => (await api.get("/cadastre360/admin/health")).data,
    enabled: tab === "health",
  });

  const resetPw = useMutation({
    mutationFn: async (id) => (await api.post(`/cadastre360/admin/users/${id}/reset-password`, { reason: "Owner-initiated reset" })).data,
    onSuccess: (d) => {
      setLastReset(d);
      toast.success(`New temp password for ${d.email}`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const unlock = useMutation({
    mutationFn: async (id) => (await api.post(`/cadastre360/admin/users/${id}/unlock`)).data,
    onSuccess: () => { toast.success("Account unlocked"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const forceChange = useMutation({
    mutationFn: async (id) => (await api.post(`/cadastre360/admin/users/${id}/force-change`)).data,
    onSuccess: () => { toast.success("User will change password on next login"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const setActivation = useMutation({
    mutationFn: async ({ id, active }) => (await api.post(`/cadastre360/admin/users/${id}/activation`, { active })).data,
    onSuccess: (d) => { toast.success(`Account ${d.status}`); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const setProvider = useMutation({
    mutationFn: async ({ key, enabled }) => (await api.post(`/cadastre360/admin/federation/providers/${key}`, { enabled })).data,
    onSuccess: (d) => { toast.success(`${d.key} ${d.enabled ? "enabled" : "disabled"}`); qc.invalidateQueries({ queryKey: ["admin-providers"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const downloadCsv = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cadastre360/admin/audit/export.csv`;
    const a = document.createElement("a");
    a.href = url; a.download = "cadastre360_audit.csv"; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const downloadJson = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cadastre360/admin/audit/export.json`;
    const a = document.createElement("a");
    a.href = url; a.download = "cadastre360_audit.json"; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const downloadPackPdf = (pack) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cadastre360/admin/audit/evidence-pack/${pack.pack_number}/pdf`;
    const a = document.createElement("a");
    a.href = url; a.download = `${pack.pack_number}.pdf`; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const sessions = useQuery({
    queryKey: ["admin-sessions"],
    queryFn: async () => (await api.get("/cadastre360/admin/sessions")).data,
    enabled: tab === "sessions",
  });
  const evidencePacks = useQuery({
    queryKey: ["admin-evidence"],
    queryFn: async () => (await api.get("/cadastre360/admin/audit/evidence-packs")).data,
    enabled: tab === "evidence",
  });
  const generatePack = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/admin/audit/evidence-pack")).data,
    onSuccess: (d) => {
      toast.success(`Evidence pack ${d.pack_number} generated`);
      qc.invalidateQueries({ queryKey: ["admin-evidence"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const terminateAll = useMutation({
    mutationFn: async (email) => (await api.post(`/cadastre360/admin/sessions/terminate-all/${encodeURIComponent(email)}`)).data,
    onSuccess: (d) => { toast.success(`Terminated ${d.terminated} session(s) for ${d.email}`); qc.invalidateQueries({ queryKey: ["admin-sessions"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div className="space-y-6" data-testid="page-admin-console">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Cadastre360™ Administration Console</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <ShieldCheck className="text-[#C99A2E]" /> Identity, Audit & Federation
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          Administrator-only workspace. Manages accounts through the existing bcrypt+JWT authentication (never replaced), reviews unified
          audit events with CSV export, and hosts the modular Identity Federation Framework (Local · Entra ID · Google Workspace · SAML
          2.0 · OIDC · LDAP), all disabled by default until an administrator configures a provider.
        </div>
      </header>

      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
            className={`px-3 py-2 rounded-sm text-[11.5px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-1.5
                        ${tab === t.key ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25"}`}>
            <t.icon size={12} className="text-[#C99A2E]" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="space-y-4" data-testid="admin-users">
          <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-2.5 text-[#7C93A5]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email / name / role"
                     data-testid="admin-user-search"
                     className="bg-[#071B2B] border cad-hairline rounded-sm pl-7 pr-2 py-1.5 text-[12px] text-[#F6F8FA] w-80" />
            </div>
            <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">{(users.data || []).length} accounts</div>
          </div>

          {lastReset && (
            <div className="cad-panel p-4 border-l-4 border-[#C99A2E]" data-testid="admin-reset-result">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Temporary password issued</div>
              <div className="text-[13px] text-[#F6F8FA] mt-1">
                <span className="font-mono-cad">{lastReset.email}</span> · new password
                <code className="mx-2 px-2 py-0.5 bg-[#071B2B] border cad-hairline rounded-sm font-mono-cad text-[#C99A2E]">{lastReset.new_password}</code>
                — deliver securely and never re-share.
              </div>
            </div>
          )}

          <div className="cad-panel p-1 overflow-x-auto">
            <table className="w-full text-[12.5px] min-w-[900px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Email", "Name", "Role", "Tenant", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(users.data || []).map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                    <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{u.email}</td>
                    <td className="px-3 py-2 text-[#DCE3E8]">{u.name}</td>
                    <td className="px-3 py-2"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{u.role}</span></td>
                    <td className="px-3 py-2 text-[11px] text-[#B3C2CC]">{u.tenant_code || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`cad-chip text-[9px] uppercase ${u.status === "disabled" ? "cad-chip-red" : "cad-chip-green"}`}>
                        {u.status || "active"}
                      </span>
                      {u.must_change_password && <span className="cad-chip cad-chip-gold text-[9px] uppercase ml-1">must change</span>}
                    </td>
                    <td className="px-3 py-2 flex flex-wrap gap-1">
                      <button onClick={() => resetPw.mutate(u.id)} data-testid={`admin-reset-${u.id}`}
                              className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                        <KeyRound size={10} /> Reset password
                      </button>
                      <button onClick={() => forceChange.mutate(u.id)} data-testid={`admin-force-${u.id}`}
                              className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                        <RefreshCw size={10} /> Force change
                      </button>
                      <button onClick={() => unlock.mutate(u.id)} data-testid={`admin-unlock-${u.id}`}
                              className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                        <Unlock size={10} /> Unlock
                      </button>
                      {u.status === "disabled"
                        ? <button onClick={() => setActivation.mutate({ id: u.id, active: true })} data-testid={`admin-activate-${u.id}`}
                                  className="cad-btn-primary px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                            <Power size={10} /> Activate
                          </button>
                        : <button onClick={() => setActivation.mutate({ id: u.id, active: false })} data-testid={`admin-deactivate-${u.id}`}
                                  className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                            <PowerOff size={10} /> Deactivate
                          </button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "sessions" && (
        <div className="space-y-3" data-testid="admin-sessions">
          <div className="cad-panel p-4 text-[12px] text-[#B3C2CC] leading-relaxed">
            View recent authentication + administration activity across all Ministry accounts. Terminate all sessions for a given email to
            immediately invalidate that user's demo session records. Existing bcrypt+JWT authentication is not modified.
          </div>
          <div className="cad-panel p-1 overflow-x-auto">
            <table className="w-full text-[12px] min-w-[720px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Timestamp", "Actor", "Action", "Entity", "Terminate"].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(sessions.data?.sessions || []).slice(0, 40).map((s) => (
                  <tr key={s.id} data-testid={`admin-session-${s.id}`}>
                    <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{(s.timestamp || s.last_seen || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{s.actor || s.email}</td>
                    <td className="px-3 py-2 text-[#DCE3E8]">{s.action || s.kind || "activity"}</td>
                    <td className="px-3 py-2 text-[11px] text-[#B3C2CC]">{s.entity || "—"}</td>
                    <td className="px-3 py-2">
                      {(s.actor || s.email) && (
                        <button onClick={() => terminateAll.mutate(s.actor || s.email)}
                                data-testid={`admin-terminate-${s.id}`}
                                className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm">Terminate all for user</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "evidence" && (
        <div className="space-y-4" data-testid="admin-evidence">
          <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
            <div className="text-[12px] text-[#B3C2CC] leading-relaxed flex-1 min-w-[240px]">
              Generate a hash-signed, QR-verified Compliance Evidence Pack aggregating all audit sources. Read-only — the underlying audit
              records are never modified.
            </div>
            <button onClick={() => generatePack.mutate()} disabled={generatePack.isPending}
                    data-testid="admin-evidence-generate"
                    className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
              {generatePack.isPending ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
              Generate evidence pack
            </button>
          </div>
          {generatePack.data && (
            <div className="cad-panel p-4 border-l-4 border-[#6BB88A]" data-testid="admin-evidence-latest">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#6BB88A]">Latest pack</div>
              <div className="text-[13px] text-[#F6F8FA] mt-1 font-mono-cad">{generatePack.data.pack_number}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2 text-[11px] font-mono-cad">
                <div><span className="text-[#7C93A5]">Audit #</span> {generatePack.data.audit_number}</div>
                <div><span className="text-[#7C93A5]">Events</span> {generatePack.data.event_count.toLocaleString()}</div>
                <div><span className="text-[#7C93A5]">Officer</span> {generatePack.data.generated_by}</div>
                <div className="sm:col-span-2 md:col-span-3"><span className="text-[#7C93A5]">SHA-256</span> {generatePack.data.content_hash}</div>
                <div className="sm:col-span-2 md:col-span-3"><span className="text-[#7C93A5]">QR verify</span> {generatePack.data.qr_verification_url}</div>
              </div>
            </div>
          )}
          <div className="cad-panel p-1 overflow-x-auto">
            <table className="w-full text-[12px] min-w-[720px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Pack #", "Audit #", "Events", "Officer", "SHA-256", "Generated", "PDF"].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(evidencePacks.data || []).map((p) => (
                  <tr key={p.id} data-testid={`admin-pack-${p.id}`}>
                    <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{p.pack_number}</td>
                    <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{p.audit_number}</td>
                    <td className="px-3 py-2 font-mono-cad text-[#C99A2E]">{p.event_count?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-[11px] text-[#B3C2CC]">{p.generated_by}</td>
                    <td className="px-3 py-2 font-mono-cad text-[10px] text-[#7C93A5]">{p.content_hash?.slice(0, 16)}…</td>
                    <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{p.generated_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => downloadPackPdf(p)} data-testid={`admin-pack-pdf-${p.id}`}
                              className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                        <Download size={10} /> Signed PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-3" data-testid="admin-audit">
          <div className="flex items-center gap-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">Sources:</div>
            {(audit.data?.sources || []).map((s) => (
              <span key={s} className="cad-chip cad-chip-blue text-[9px] uppercase">{s.replace(/_/g, " ")}</span>
            ))}
            <button onClick={downloadCsv} data-testid="admin-audit-export"
                    className="cad-btn-primary ml-auto px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
              <Download size={11} /> Export CSV
            </button>
            <button onClick={downloadJson} data-testid="admin-audit-export-json"
                    className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
              <Download size={11} /> Export JSON
            </button>
          </div>
          <div className="cad-panel p-1 overflow-x-auto">
            <table className="w-full text-[12px] min-w-[900px]">
              <thead><tr className="text-left border-b cad-hairline">
                {["Timestamp", "Source", "Actor", "Action", "Entity", "Reason"].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                ))}
              </tr></thead>
              <tbody className="cad-tbody">
                {(audit.data?.events || []).slice(0, 100).map((e) => (
                  <tr key={e.id} data-testid={`admin-audit-${e.id}`}>
                    <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{e.timestamp?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-2"><span className="cad-chip cad-chip-blue text-[9px] uppercase">{e.source}</span></td>
                    <td className="px-3 py-2 font-mono-cad text-[11px] text-[#DCE3E8]">{e.actor}</td>
                    <td className="px-3 py-2 text-[#F6F8FA]">{e.action}</td>
                    <td className="px-3 py-2 text-[#B3C2CC] text-[11px]">{e.entity}/{(e.entity_id || "").slice(-8)}</td>
                    <td className="px-3 py-2 text-[11px] text-[#B3C2CC]">{e.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "federation" && (
        <div className="space-y-3" data-testid="admin-federation">
          <div className="text-[13px] text-[#B3C2CC] leading-relaxed max-w-4xl">
            Enterprise Identity Federation Framework — modular adapters that stay <b>disabled by default</b>. Activating any provider
            other than <code className="font-mono-cad">local</code> is a controlled administrator action and follows the approved
            integration playbook (Emergent Google Auth, Microsoft Entra ID configuration, SAML metadata, OIDC discovery, LDAP bind).
            Enabling here only lights up the adapter — the actual OAuth/SAML flow requires the corresponding secrets in secure config.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(providers.data || []).map((p) => (
              <div key={p.key} className="cad-panel p-4" data-testid={`admin-provider-${p.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-bold text-[#F6F8FA] text-[15px]">{p.name}</div>
                    <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#C99A2E] mt-0.5">{p.key} · {p.kind}</div>
                  </div>
                  <span className={`cad-chip text-[9px] uppercase ${p.enabled ? "cad-chip-green" : "cad-chip-red"}`}>
                    {p.enabled ? "enabled" : "disabled"}
                  </span>
                </div>
                <div className="text-[12px] text-[#B3C2CC] mt-2 leading-relaxed">{p.notes}</div>
                {p.key !== "local" && (
                  <button onClick={() => setProvider.mutate({ key: p.key, enabled: !p.enabled })}
                          data-testid={`admin-provider-toggle-${p.key}`}
                          className="cad-btn-outline mt-3 px-3 py-1 text-[11px] rounded-sm inline-flex items-center gap-1">
                    {p.enabled ? <Lock size={10} /> : <Unlock size={10} />} {p.enabled ? "Disable adapter" : "Enable adapter"}
                  </button>
                )}
                {p.key === "local" && (
                  <div className="mt-3 text-[10px] font-mono-cad uppercase tracking-widest text-[#5F7688]">Cannot be disabled</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "health" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-health">
          <KPI label="Status" value={health.data?.status ?? "—"} testid="admin-health-status" />
          <KPI label="Users" value={(health.data?.users ?? 0).toLocaleString()} testid="admin-health-users" />
          <KPI label="Tenants" value={(health.data?.tenants ?? 0).toLocaleString()} testid="admin-health-tenants" />
          <KPI label="Identity providers" value={(health.data?.identity_providers ?? 0).toLocaleString()} testid="admin-health-providers" />
          <KPI label="Collections" value={(health.data?.collections_total ?? 0).toLocaleString()} testid="admin-health-collections" />
          <KPI label="Checked at" value={health.data?.checked_at?.slice(11, 19) || "—"} testid="admin-health-checked" />
        </div>
      )}

      {tab === "infra" && <InfraPanel />}
      {tab === "monitoring" && <MonitoringPanel />}
      {tab === "validation" && <ValidationPanel />}
      {tab === "flags" && <FlagsPanel />}
      {tab === "enterprise-trust" && <EnterpriseTrustPanel />}
      {tab === "tsa" && <TSAPanel />}
      {tab === "security-monitoring" && <SecurityMonitoringPanel />}
      {tab === "cert-lifecycle" && <CertLifecyclePanel />}
      {tab === "disaster-recovery" && <DisasterRecoveryPanel />}
      {tab === "production-audit" && <ProductionAuditPanel />}
    </div>
  );
};

const StatusChip = ({ ok, labelOk = "operational", labelBad = "offline" }) => (
  <span className={"cad-chip text-[9px] uppercase inline-flex items-center gap-1 " + (ok ? "cad-chip-green" : "cad-chip-red")}>
    {ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />} {ok ? labelOk : labelBad}
  </span>
);

const InfraPanel = () => {
  const health = useQuery({
    queryKey: ["infra-health-centre"],
    queryFn: async () => (await api.get("/cadastre360/infra/health-centre")).data,
    refetchInterval: 15000,
  });
  const h = health.data || {};
  const redisValue = h.redis?.connected ? "connected" : (h.redis?.url_configured ? "fallback → mongo" : "not configured");
  const redisHint = h.redis?.server_version ? ("v" + h.redis.server_version) : (h.redis?.last_error || "mongo fallback active");
  const pkiHint = h.pki?.signer?.days_remaining ? (h.pki.signer.days_remaining + "d remaining") : "—";
  const uptimeValue = h.uptime_seconds ? (Math.floor(h.uptime_seconds / 60) + "m") : "—";
  const cpuValue = h.system ? (h.system.cpu_percent + "%") : "—";
  const memValue = h.system ? (h.system.memory_percent + "%") : "—";
  const memHint = h.system ? (h.system.memory_used_mb + "/" + h.system.memory_total_mb + " MB") : "";
  const diskValue = h.system ? (h.system.disk_percent + "%") : "—";
  const diskHint = h.system ? (h.system.disk_used_gb + "/" + h.system.disk_total_gb + " GB") : "";
  return (
    <div className="space-y-4" data-testid="admin-infra">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Redis" value={redisValue} testid="infra-kpi-redis" hint={redisHint} />
        <KPI label="PKI signer" value={h.pki?.available ? "available" : "unavailable"} testid="infra-kpi-pki" hint={pkiHint} />
        <KPI label="PDF engine" value={h.pdf_engine?.available ? "operational" : "offline"} testid="infra-kpi-pdf" hint={h.pdf_engine?.signing || ""} />
        <KPI label="Verification portal" value={h.verification_portal?.available ? "online" : "offline"} testid="infra-kpi-portal" hint={h.verification_portal?.path} />
        <KPI label="Federation providers" value={(h.federation?.enabled ?? 0) + "/" + (h.federation?.total ?? 0)} testid="infra-kpi-fed" />
        <KPI label="Evidence queue" value={h.queues?.evidence ?? 0} testid="infra-kpi-q-evidence" />
        <KPI label="Audit queue" value={(h.queues?.audit ?? 0).toLocaleString?.() || 0} testid="infra-kpi-q-audit" />
        <KPI label="PKI signatures" value={h.queues?.pki_signatures ?? 0} testid="infra-kpi-q-pki" />
        <KPI label="Uptime" value={uptimeValue} testid="infra-kpi-uptime" />
        <KPI label="CPU" value={cpuValue} testid="infra-kpi-cpu" />
        <KPI label="Memory" value={memValue} testid="infra-kpi-mem" hint={memHint} />
        <KPI label="Disk" value={diskValue} testid="infra-kpi-disk" hint={diskHint} />
      </div>

      <div className="cad-panel p-4" data-testid="infra-redis-panel">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
          <Database size={12} /> Redis production activation
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-[12px]">
          <div><span className="text-[#7C93A5]">URL configured</span> <StatusChip ok={h.redis?.url_configured} labelOk="yes" labelBad="not set" /></div>
          <div><span className="text-[#7C93A5]">Connected</span> <StatusChip ok={h.redis?.connected} /></div>
          <div><span className="text-[#7C93A5]">TLS</span> <StatusChip ok={h.redis?.tls} labelOk="tls" labelBad="none" /></div>
          <div><span className="text-[#7C93A5]">Fallback</span> <span className="cad-chip cad-chip-blue text-[9px] uppercase">mongo</span></div>
          <div><span className="text-[#7C93A5]">Version</span> <span className="font-mono-cad text-[#F6F8FA]">{h.redis?.server_version || "—"}</span></div>
          <div><span className="text-[#7C93A5]">Uptime</span> <span className="font-mono-cad text-[#F6F8FA]">{h.redis?.uptime_seconds || "—"}</span></div>
        </div>
        {h.redis?.last_error && (
          <div className="text-[11px] text-[#B3C2CC] mt-2 font-mono-cad">Last error: {h.redis.last_error}</div>
        )}
        <div className="text-[11px] text-[#7C93A5] mt-3 font-mono-cad">
          Set <code className="font-mono-cad text-[#C99A2E]">REDIS_URL</code> in backend/.env to activate; the platform automatically falls back to MongoDB whenever Redis is unavailable — authentication, JWT and existing workflows remain fully operational.
        </div>
      </div>

      <div className="cad-panel p-4" data-testid="infra-pki-panel">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
          <ShieldCheck size={12} /> Enterprise PKI · Ministry Chain of Trust
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-[12px]">
          <div><span className="text-[#7C93A5]">Root CA</span> <span className="font-mono-cad text-[#F6F8FA] break-all">{h.pki?.root?.common_name || "—"}</span></div>
          <div><span className="text-[#7C93A5]">Signer</span> <span className="font-mono-cad text-[#F6F8FA] break-all">{h.pki?.signer?.common_name || "—"}</span></div>
          <div><span className="text-[#7C93A5]">Signer expires</span> <span className="font-mono-cad text-[#F6F8FA]">{h.pki?.signer?.not_after || "—"} ({h.pki?.signer?.days_remaining ?? "—"}d)</span></div>
          <div><span className="text-[#7C93A5]">Algorithm</span> <span className="font-mono-cad text-[#F6F8FA]">{h.pki?.signer?.signature_algorithm || "—"}</span></div>
          <div className="md:col-span-2"><span className="text-[#7C93A5]">Signer fingerprint</span> <span className="font-mono-cad text-[10px] text-[#B3C2CC] break-all">{h.pki?.signer?.sha256_fingerprint}</span></div>
          <div><span className="text-[#7C93A5]">Revocation</span> <StatusChip ok={h.pki?.revocation?.status === "operational"} labelOk={h.pki?.revocation?.status} labelBad="unknown" /></div>
          <div><span className="text-[#7C93A5]">RFC3161</span> <span className="cad-chip cad-chip-blue text-[9px] uppercase">{h.pki?.rfc3161_timestamp}</span></div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={process.env.REACT_APP_BACKEND_URL + "/api/cadastre360/infra/pki/root.crt"} target="_blank" rel="noreferrer"
             className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="infra-pki-root-download">
            <Download size={11} /> Download Ministry root.crt
          </a>
          <a href={process.env.REACT_APP_BACKEND_URL + "/api/cadastre360/infra/pki/signing.crt"} target="_blank" rel="noreferrer"
             className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="infra-pki-signer-download">
            <Download size={11} /> Download signer.crt
          </a>
        </div>
      </div>
    </div>
  );
};

const MonitoringPanel = () => {
  const summary = useQuery({
    queryKey: ["infra-monitoring-summary"],
    queryFn: async () => (await api.get("/cadastre360/infra/monitoring/summary")).data,
    refetchInterval: 20000,
  });
  const events = useQuery({
    queryKey: ["infra-monitoring-events"],
    queryFn: async () => (await api.get("/cadastre360/infra/monitoring/events?limit=80")).data,
    refetchInterval: 20000,
  });
  const s = summary.data || {};
  return (
    <div className="space-y-4" data-testid="admin-monitoring">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Redis available" value={s.redis_available ? "yes" : "no"} testid="mon-kpi-redis" />
        <KPI label="Cert expiry (days)" value={s.certificate_expires_in_days ?? "—"} testid="mon-kpi-cert" />
        <KPI label="Federation enabled" value={s.federation_active_providers ?? 0} testid="mon-kpi-fed" />
        <KPI label="Verification portal" value={s.verification_portal_online ? "online" : "offline"} testid="mon-kpi-portal" />
        <KPI label="PDF healthy" value={s.pdf_generation_healthy ? "yes" : "no"} testid="mon-kpi-pdf" />
        <KPI label="Signatures healthy" value={s.digital_signatures_healthy ? "yes" : "no"} testid="mon-kpi-sig" />
        <KPI label="Queue health" value={s.queue_health || "—"} testid="mon-kpi-q" />
        <KPI label="Security events (24h)" value={s.security_events_last_24h ?? 0} testid="mon-kpi-sec" />
      </div>
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[720px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Timestamp", "Actor", "Action", "Entity", "Metadata"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(events.data || []).map((e) => (
              <tr key={e.id} data-testid={`mon-event-${e.id}`}>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{e.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{e.actor}</td>
                <td className="px-3 py-2 text-[#DCE3E8]">{e.action}</td>
                <td className="px-3 py-2 text-[11px] text-[#B3C2CC]">{e.entity}/{(e.entity_id || "").slice(-10)}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#7C93A5] max-w-[260px] truncate">{JSON.stringify(e.metadata || {})}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ValidationPanel = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["infra-validation"],
    queryFn: async () => (await api.get("/cadastre360/infra/validation/reports")).data,
  });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/infra/validation/run")).data,
    onSuccess: (d) => { toast.success(`Validation ${d.overall} · ${d.passed} passed`); qc.invalidateQueries({ queryKey: ["infra-validation"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const latest = list.data?.[0];
  return (
    <div className="space-y-4" data-testid="admin-validation">
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] leading-relaxed flex-1 min-w-[240px]">
          Run the zero-regression production security validation. Every check is written to an immutable, hash-signed audit report and can be replayed by executive auditors.
        </div>
        <button onClick={() => run.mutate()} disabled={run.isPending}
                className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
                data-testid="admin-validation-run">
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />}
          Run validation
        </button>
      </div>
      {latest && (
        <div className="cad-panel p-4" data-testid="admin-validation-latest">
          <div className="flex items-center gap-3">
            <span className={`cad-chip text-[10px] uppercase ${latest.overall === "PASS" ? "cad-chip-green" : "cad-chip-red"}`}>{latest.overall}</span>
            <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{latest.id}</span>
            <span className="font-mono-cad text-[11px] text-[#B3C2CC]">{latest.generated_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {(latest.checks || []).map((c, i) => (
              <div key={i} className="border-b cad-hairline pb-2">
                <div className="flex items-center gap-2">
                  <StatusChip ok={c.status === "pass"} labelOk={c.status} labelBad={c.status} />
                  <div className="text-[12px] text-[#F6F8FA]">{c.name}</div>
                </div>
                <div className="text-[10px] text-[#B3C2CC] font-mono-cad mt-0.5">{c.detail}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-[#7C93A5] font-mono-cad break-all">signature: {latest.signature}</div>
        </div>
      )}
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[600px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Report", "Result", "Passed", "Failed", "Warnings", "Generated"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(list.data || []).map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#F6F8FA]">{r.id}</td>
                <td className="px-3 py-2"><span className={`cad-chip text-[9px] uppercase ${r.overall === "PASS" ? "cad-chip-green" : "cad-chip-red"}`}>{r.overall}</span></td>
                <td className="px-3 py-2 font-mono-cad text-[#6BB88A]">{r.passed}</td>
                <td className="px-3 py-2 font-mono-cad text-[#E67E7E]">{r.failed}</td>
                <td className="px-3 py-2 font-mono-cad text-[#C99A2E]">{r.warnings}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{r.generated_at?.slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FlagsPanel = () => {
  const qc = useQueryClient();
  const flags = useQuery({
    queryKey: ["infra-flags"],
    queryFn: async () => (await api.get("/cadastre360/infra/feature-flags")).data,
  });
  const toggle = useMutation({
    mutationFn: async ({ name, enabled }) => (await api.post(`/cadastre360/infra/feature-flags/${name}`, { enabled })).data,
    onSuccess: (d) => { toast.success(`${d.name} ${d.enabled ? "enabled" : "disabled"}`); qc.invalidateQueries({ queryKey: ["infra-flags"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  return (
    <div className="space-y-3" data-testid="admin-flags">
      <div className="cad-panel p-4 text-[12px] text-[#B3C2CC] leading-relaxed">
        Enterprise rollback protection. Every infrastructure feature is flag-controlled. Disabling any of these switches the platform
        back to pre-activation behaviour <b>without downtime</b> — Redis falls back to Mongo, PKI signing becomes optional, the
        public verification portal returns 503, and existing PDFs, JWT authentication and administration continue exactly as before.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(flags.data || {}).map(([k, v]) => (
          <div key={k} className="cad-panel p-4" data-testid={`flag-card-${k}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-bold text-[#F6F8FA] text-[13px]">{k.replace(/^cadastre360_/, "").replace(/_/g, " ")}</div>
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#C99A2E] mt-0.5">{k}</div>
              </div>
              <span className={`cad-chip text-[9px] uppercase ${v ? "cad-chip-green" : "cad-chip-red"}`}>{v ? "on" : "off"}</span>
            </div>
            <button onClick={() => toggle.mutate({ name: k, enabled: !v })}
                    className="cad-btn-outline mt-3 px-3 py-1 text-[11px] rounded-sm inline-flex items-center gap-1"
                    data-testid={`flag-toggle-${k}`}>
              {v ? <ToggleRight size={11} /> : <ToggleLeft size={11} />} {v ? "Disable (rollback)" : "Enable"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const EnterpriseTrustPanel = () => {
  const qc = useQueryClient();
  const cert = useQuery({
    queryKey: ["sec-cert-history"],
    queryFn: async () => (await api.get("/cadastre360/security/certificates/history")).data,
  });
  const trust = useQuery({
    queryKey: ["sec-pdf-trust"],
    queryFn: async () => (await api.get("/cadastre360/security/pdf-trust/summary")).data,
  });
  const recordVersion = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/security/certificates/record-version")).data,
    onSuccess: () => { toast.success("Certificate version recorded"); qc.invalidateQueries({ queryKey: ["sec-cert-history"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const current = cert.data?.current || {};
  const s = trust.data || {};
  return (
    <div className="space-y-4" data-testid="admin-enterprise-trust">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Signed evidence packs" value={s.signed_evidence_packs ?? 0} testid="trust-kpi-signed" />
        <KPI label="External TSA stamps" value={s.external_tsa_timestamps ?? 0} testid="trust-kpi-tsa" />
        <KPI label="Adobe compatible" value={s.adobe_trusted_signature_compatible ? "yes" : "no"} testid="trust-kpi-adobe" />
        <KPI label="Cert chain available" value={s.certificate_chain_available ? "yes" : "no"} testid="trust-kpi-chain" />
      </div>
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
          <ShieldCheck size={12} /> Ministry PKI · Current chain
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-[12px]">
          <div><span className="text-[#7C93A5]">Root</span> <span className="font-mono-cad text-[#F6F8FA] break-all">{current?.root?.common_name || "—"}</span></div>
          <div><span className="text-[#7C93A5]">Signer</span> <span className="font-mono-cad text-[#F6F8FA] break-all">{current?.signer?.common_name || "—"}</span></div>
          <div><span className="text-[#7C93A5]">Root not after</span> <span className="font-mono-cad text-[#F6F8FA]">{current?.root?.not_after || "—"} ({current?.root?.days_remaining ?? "—"}d)</span></div>
          <div><span className="text-[#7C93A5]">Signer not after</span> <span className="font-mono-cad text-[#F6F8FA]">{current?.signer?.not_after || "—"} ({current?.signer?.days_remaining ?? "—"}d)</span></div>
          <div className="md:col-span-2"><span className="text-[#7C93A5]">Signer fingerprint</span> <span className="font-mono-cad text-[10px] text-[#B3C2CC] break-all">{current?.signer?.sha256_fingerprint || "—"}</span></div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={API + "/cadastre360/security/certificates/root.crt"} target="_blank" rel="noreferrer"
             className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="trust-download-root">
            <Download size={11} /> Ministry root.crt
          </a>
          <a href={API + "/cadastre360/security/certificates/signing.crt"} target="_blank" rel="noreferrer"
             className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="trust-download-signing">
            <Download size={11} /> signing.crt
          </a>
          <a href="/certificates" target="_blank" rel="noreferrer"
             className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="trust-open-portal">
            <ExternalLink size={11} /> Public certificate portal
          </a>
          <button onClick={() => recordVersion.mutate()} disabled={recordVersion.isPending}
                  className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="trust-record-version">
            {recordVersion.isPending ? <Loader2 size={11} className="animate-spin" /> : <BadgeCheck size={11} />}
            Record version snapshot
          </button>
        </div>
      </div>
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[640px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Recorded", "By", "Root fingerprint", "Signer fingerprint", "Signer expires"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(cert.data?.history || []).map((h) => (
              <tr key={h.id}>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{h.recorded_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{h.recorded_by}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC] truncate max-w-[220px]">{h.root_fingerprint}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC] truncate max-w-[220px]">{h.signer_fingerprint}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#C99A2E]">{h.signer_not_after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TSAPanel = () => {
  const qc = useQueryClient();
  const [digest, setDigest] = useState("");
  const [provider, setProvider] = useState("");
  const health = useQuery({
    queryKey: ["sec-tsa-health"],
    queryFn: async () => (await api.get("/cadastre360/security/tsa/health")).data,
  });
  const history = useQuery({
    queryKey: ["sec-tsa-history"],
    queryFn: async () => (await api.get("/cadastre360/security/tsa/history")).data,
  });
  const request = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/security/tsa/request", { digest, provider: provider || undefined })).data,
    onSuccess: (d) => {
      toast[d.success ? "success" : "warning"](d.success ? ("TSA " + d.provider + " OK") : ("Fell back to self attestation"));
      qc.invalidateQueries({ queryKey: ["sec-tsa-history"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  return (
    <div className="space-y-4" data-testid="admin-tsa">
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
          <Clock size={12} /> RFC 3161 Timestamp Authority
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          {(health.data?.providers || []).map((p) => (
            <div key={p.key} className="cad-panel p-3" data-testid={"tsa-provider-" + p.key}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-bold text-[#F6F8FA] text-[13px]">{p.name}</div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#C99A2E] mt-0.5">{p.key}</div>
                </div>
                <span className={"cad-chip text-[9px] uppercase " + (p.enabled ? "cad-chip-green" : "cad-chip-red")}>{p.enabled ? "on" : "off"}</span>
              </div>
              <div className="text-[10px] font-mono-cad text-[#B3C2CC] mt-2 break-all">{p.url || "(not configured)"}</div>
              <div className="text-[10px] font-mono-cad text-[#7C93A5] mt-1">policy: {p.policy_oid || "—"}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-[#B3C2CC] font-mono-cad">
          Active: <span className="text-[#C99A2E]">{health.data?.active?.key || "self-attestation fallback"}</span>
        </div>
      </div>

      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Test a timestamp request</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <input value={digest} onChange={(e) => setDigest(e.target.value)} placeholder="SHA-256 hex digest"
                 data-testid="tsa-digest-input"
                 className="bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono-cad md:col-span-2" />
          <select value={provider} onChange={(e) => setProvider(e.target.value)}
                  data-testid="tsa-provider-select"
                  className="bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono-cad">
            <option value="">Auto (preferred provider)</option>
            {(health.data?.providers || []).map((p) => (
              <option key={p.key} value={p.key}>{p.name}</option>
            ))}
          </select>
        </div>
        <button onClick={() => request.mutate()} disabled={!digest || request.isPending}
                data-testid="tsa-request-btn"
                className="cad-btn-primary mt-3 px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
          {request.isPending ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />} Request timestamp
        </button>
      </div>

      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[640px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Requested", "By", "Provider", "Result", "Digest"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(history.data?.requests || []).map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{r.requested_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{r.requested_by}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#C99A2E]">{r.provider || "—"}</td>
                <td className="px-3 py-2"><StatusChip ok={r.success} labelOk="rfc3161" labelBad="self attestation" /></td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC] truncate max-w-[220px]">{r.digest}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SecurityMonitoringPanel = () => {
  const summary = useQuery({
    queryKey: ["sec-mon-exec"],
    queryFn: async () => (await api.get("/cadastre360/security/monitoring/executive-summary")).data,
    refetchInterval: 30000,
  });
  const alerts = useQuery({
    queryKey: ["sec-mon-alerts"],
    queryFn: async () => (await api.get("/cadastre360/security/monitoring/alerts")).data,
    refetchInterval: 30000,
  });
  const audit = useQuery({
    queryKey: ["sec-audit-log"],
    queryFn: async () => (await api.get("/cadastre360/security/audit-log?limit=100")).data,
  });
  const chain = useQuery({
    queryKey: ["sec-audit-chain"],
    queryFn: async () => (await api.get("/cadastre360/security/audit-log/verify-chain")).data,
  });
  const s = summary.data || {};
  return (
    <div className="space-y-4" data-testid="admin-security-monitoring">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Failed logins (24h)" value={s.failed_logins_24h ?? 0} testid="secmon-fails" />
        <KPI label="Lockouts (7d)" value={s.lockouts_7d ?? 0} testid="secmon-lockouts" />
        <KPI label="Privilege escalations (7d)" value={s.privilege_escalations_7d ?? 0} testid="secmon-esc" />
        <KPI label="Suspicious sessions (7d)" value={s.suspicious_sessions_7d ?? 0} testid="secmon-susp" />
        <KPI label="Expired certificates" value={s.expired_certificates ?? 0} testid="secmon-expired" />
        <KPI label="Cert days remaining" value={s.certificate_days_remaining ?? 0} testid="secmon-days" />
        <KPI label="Redis" value={s.redis_healthy ? "up" : "fallback"} testid="secmon-redis" />
        <KPI label="PKI" value={s.pki_healthy ? "up" : "down"} testid="secmon-pki" />
        <KPI label="TSA" value={s.tsa_healthy ? "up" : "self"} testid="secmon-tsa" />
        <KPI label="Evidence (7d)" value={s.evidence_generated_7d ?? 0} testid="secmon-evi" />
        <KPI label="Audit exports (7d)" value={s.audit_exports_7d ?? 0} testid="secmon-exp" />
        <KPI label="Security events (24h)" value={s.security_events_24h ?? 0} testid="secmon-events" />
      </div>

      <div className="cad-panel p-4" data-testid="secmon-chain">
        <div className="flex items-center gap-2">
          <StatusChip ok={chain.data?.chain_intact} labelOk="hash chain intact" labelBad="chain broken" />
          <div className="text-[11px] text-[#B3C2CC] font-mono-cad">
            {chain.data?.events_verified ?? 0} events verified · algorithm SHA-256
          </div>
        </div>
        {chain.data?.broken_at && (
          <div className="text-[11px] text-[#E67E7E] font-mono-cad mt-1 flex items-center gap-1">
            <AlertTriangle size={11} /> Chain broken at event: {chain.data.broken_at}
          </div>
        )}
      </div>

      <div className="cad-panel p-4" data-testid="secmon-alerts">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Live alerts</div>
        <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto">
          {(alerts.data?.alerts || []).map((a) => (
            <div key={a.id} className="border-b cad-hairline pb-2">
              <div className="flex items-center gap-2 text-[11px]">
                <span className={"cad-chip text-[9px] uppercase " + (a.severity === "critical" ? "cad-chip-red" : (a.severity === "warning" ? "cad-chip-gold" : "cad-chip-blue"))}>{a.severity}</span>
                <span className="font-mono-cad text-[#F6F8FA]">{a.action}</span>
                <span className="font-mono-cad text-[#B3C2CC]">{a.entity}/{(a.entity_id || "").slice(-10)}</span>
                <span className="font-mono-cad text-[10px] text-[#7C93A5] ml-auto">{a.timestamp?.slice(0, 19).replace("T", " ")}</span>
              </div>
              <div className="text-[10px] font-mono-cad text-[#7C93A5]">actor: {a.actor} · ip: {a.source_ip || "—"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="cad-panel p-1 overflow-x-auto" data-testid="secmon-audit-table">
        <table className="w-full text-[12px] min-w-[720px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Time", "Severity", "Actor", "Action", "Entity", "IP", "Event hash"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(audit.data?.events || []).slice(0, 80).map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{e.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2"><span className={"cad-chip text-[9px] uppercase " + (e.severity === "critical" ? "cad-chip-red" : (e.severity === "warning" ? "cad-chip-gold" : "cad-chip-blue"))}>{e.severity}</span></td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{e.actor}</td>
                <td className="px-3 py-2 text-[#DCE3E8]">{e.action}</td>
                <td className="px-3 py-2 text-[11px] text-[#B3C2CC]">{e.entity}/{(e.entity_id || "").slice(-10)}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#7C93A5]">{e.source_ip || "—"}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#7C93A5] truncate max-w-[140px]">{e.event_hash}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CertLifecyclePanel = () => {
  const qc = useQueryClient();
  const life = useQuery({
    queryKey: ["trust-cert-lifecycle"],
    queryFn: async () => (await api.get("/cadastre360/trust/cert-lifecycle")).data,
  });
  const reminders = useQuery({
    queryKey: ["trust-cert-reminders"],
    queryFn: async () => (await api.get("/cadastre360/trust/cert-lifecycle/reminders")).data,
  });
  const send = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/trust/cert-lifecycle/send-reminder")).data,
    onSuccess: () => { toast.success("Expiry reminder queued"); qc.invalidateQueries({ queryKey: ["trust-cert-reminders"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const d = life.data || {};
  return (
    <div className="space-y-4" data-testid="admin-cert-lifecycle">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Signer days" value={d.expiry_dashboard?.signer_days_remaining ?? "—"} hint={d.expiry_dashboard?.signer_urgency} testid="cl-signer-days" />
        <KPI label="Root days" value={d.expiry_dashboard?.root_days_remaining ?? "—"} hint={d.expiry_dashboard?.root_urgency} testid="cl-root-days" />
        <KPI label="Renewals due" value={d.renewal_schedule?.length ?? 0} testid="cl-renewals" />
        <KPI label="Reminders sent" value={d.reminders_sent_total ?? 0} testid="cl-reminders" />
      </div>
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
          <KeyRound size={12} /> Renewal schedule
        </div>
        <div className="mt-3 space-y-2">
          {(d.renewal_schedule || []).length === 0 && (
            <div className="text-[12px] text-[#B3C2CC]">No renewals due in the observation window.</div>
          )}
          {(d.renewal_schedule || []).map((r, i) => (
            <div key={r.cert + "-" + i} className="border-b cad-hairline pb-2 flex items-center gap-3">
              <span className={"cad-chip text-[9px] uppercase " + (r.urgency === "high" ? "cad-chip-red" : (r.urgency === "medium" ? "cad-chip-gold" : "cad-chip-blue"))}>{r.urgency}</span>
              <span className="font-mono-cad text-[#F6F8FA]">{r.cert}</span>
              <span className="text-[12px] text-[#DCE3E8]">{r.action}</span>
              <span className="ml-auto font-mono-cad text-[11px] text-[#C99A2E]">{r.due_in_days}d</span>
            </div>
          ))}
        </div>
        <button onClick={() => send.mutate()} disabled={send.isPending}
                className="cad-btn-primary mt-3 px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
                data-testid="cl-send-reminder">
          {send.isPending ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Send expiry reminder
        </button>
      </div>
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[500px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Sent", "By", "Signer days", "Root days"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(reminders.data || []).map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{r.created_by}</td>
                <td className="px-3 py-2 font-mono-cad text-[#C99A2E]">{r.signer_days}</td>
                <td className="px-3 py-2 font-mono-cad text-[#C99A2E]">{r.root_days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DisasterRecoveryPanel = () => {
  const qc = useQueryClient();
  const history = useQuery({
    queryKey: ["trust-dr-history"],
    queryFn: async () => (await api.get("/cadastre360/trust/disaster-recovery/history")).data,
  });
  const runTest = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/trust/disaster-recovery/test")).data,
    onSuccess: (d) => { toast.success("DR test: " + d.outcome); qc.invalidateQueries({ queryKey: ["trust-dr-history"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const doExport = async () => {
    const res = await api.post("/cadastre360/trust/disaster-recovery/export",
      { include_feature_flags: true, include_certificates_metadata: true, include_audit_summary: true, include_evidence_summary: true },
      { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "cadastre360_dr_export.json"; document.body.appendChild(a); a.click(); a.remove();
    toast.success("Configuration exported");
    qc.invalidateQueries({ queryKey: ["trust-dr-history"] });
  };
  return (
    <div className="space-y-4" data-testid="admin-dr">
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[220px] leading-relaxed">
          Verify backup readiness, export sovereign configuration, and run disaster-recovery smoke tests. Existing production data
          is never modified.
        </div>
        <button onClick={doExport} className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="dr-export">
          <Download size={11} /> Export configuration
        </button>
        <button onClick={() => runTest.mutate()} disabled={runTest.isPending}
                className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="dr-run-test">
          {runTest.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run DR test
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="cad-panel p-4" data-testid="dr-tests-panel">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Recent DR tests</div>
          <div className="mt-2 space-y-2">
            {(history.data?.tests || []).map((t) => (
              <div key={t.id} className="border-b cad-hairline pb-2">
                <div className="flex items-center gap-2">
                  <span className={"cad-chip text-[9px] uppercase " + (t.outcome === "READY" ? "cad-chip-green" : "cad-chip-red")}>{t.outcome}</span>
                  <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{t.id}</span>
                  <span className="font-mono-cad text-[10px] text-[#B3C2CC] ml-auto">{t.run_at?.slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="text-[10px] text-[#B3C2CC] font-mono-cad">{t.passed}/{(t.passed + t.failed)} checks · by {t.run_by}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="cad-panel p-4" data-testid="dr-exports-panel">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Configuration exports</div>
          <div className="mt-2 space-y-2">
            {(history.data?.exports || []).map((e) => (
              <div key={e.id} className="border-b cad-hairline pb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{e.id}</span>
                  <span className="font-mono-cad text-[10px] text-[#C99A2E] ml-auto">{Math.round((e.size_bytes || 0) / 1024)} kB</span>
                </div>
                <div className="text-[10px] text-[#7C93A5] font-mono-cad break-all">sha256: {e.sha256}</div>
                <div className="text-[10px] text-[#B3C2CC] font-mono-cad">{e.exported_at?.slice(0, 19).replace("T", " ")} · {e.exported_by}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductionAuditPanel = () => {
  const qc = useQueryClient();
  const audit = useQuery({
    queryKey: ["trust-production-audit"],
    queryFn: async () => (await api.get("/cadastre360/trust/production-audit")).data,
  });
  const acceptance = useQuery({
    queryKey: ["trust-acceptance-reports"],
    queryFn: async () => (await api.get("/cadastre360/trust/acceptance-audit/reports")).data,
  });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/trust/acceptance-audit/run", {})).data,
    onSuccess: (d) => { toast.success("Acceptance " + d.overall + " · " + d.passed + "/" + d.scope_size); qc.invalidateQueries({ queryKey: ["trust-acceptance-reports"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const a = audit.data || {};
  const latest = (acceptance.data || [])[0];
  return (
    <div className="space-y-4" data-testid="admin-production-audit">
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[220px] leading-relaxed">
          Generate Production Readiness Certificate and run the full Executive Acceptance Audit against every registered module.
        </div>
        <a href={API + "/cadastre360/trust/production-audit/pdf"} target="_blank" rel="noreferrer"
           className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="pa-download-pdf">
          <Download size={11} /> Download PDF certificate
        </a>
        <a href={API + "/cadastre360/trust/production-audit/html"} target="_blank" rel="noreferrer"
           className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="pa-view-html">
          <ExternalLink size={11} /> View HTML report
        </a>
        <button onClick={() => run.mutate()} disabled={run.isPending}
                className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="pa-run-acceptance">
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run acceptance audit
        </button>
      </div>

      {a.executive_summary && (
        <div className="cad-panel p-4" data-testid="pa-executive-summary">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Executive summary</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
            <div><span className="text-[#7C93A5]">Verdict</span> <span className="cad-chip cad-chip-green text-[9px] uppercase">{a.executive_summary.verdict}</span></div>
            <div><span className="text-[#7C93A5]">Algorithm</span> <span className="font-mono-cad text-[#F6F8FA]">{a.executive_summary.signature_algorithm}</span></div>
            <div><span className="text-[#7C93A5]">Root days</span> <span className="font-mono-cad text-[#C99A2E]">{a.executive_summary.pki_root_days_remaining}</span></div>
            <div><span className="text-[#7C93A5]">Signer days</span> <span className="font-mono-cad text-[#C99A2E]">{a.executive_summary.signer_days_remaining}</span></div>
          </div>
          <div className="text-[10px] font-mono-cad text-[#7C93A5] mt-2 break-all">signature: {a.signature}</div>
        </div>
      )}

      {latest && (
        <div className="cad-panel p-4" data-testid="pa-acceptance-latest">
          <div className="flex items-center gap-3">
            <span className={"cad-chip text-[10px] uppercase " + (latest.overall === "PASS" ? "cad-chip-green" : "cad-chip-red")}>{latest.overall}</span>
            <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{latest.id}</span>
            <span className="font-mono-cad text-[11px] text-[#B3C2CC]">{latest.passed}/{latest.scope_size} checks</span>
            <span className="font-mono-cad text-[10px] text-[#7C93A5] ml-auto">{latest.run_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            {(latest.checks || []).map((c, i) => (
              <div key={c.name + "-" + i} className="border-b cad-hairline pb-1 flex items-center gap-2">
                {c.ok ? <CheckCircle2 size={11} className="text-[#6BB88A]" /> : <XCircle size={11} className="text-[#E67E7E]" />}
                <span className="text-[11px] text-[#F6F8FA]">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

